/**
 * App banners — the promotional cards on every customer's home screen.
 *
 * Superadmin only, and gated that way for a reason worth stating: a banner is copy, an image,
 * a colour scheme, and a *tap that goes somewhere*. Each of those four is a way to reach every
 * customer at once, and three of them can be got wrong in ways the operator cannot see from
 * where they are standing.
 *
 * ## The destination is two different things, kept apart
 *
 * **In the app**, it is a picker over a closed list, never a text field. The API accepts only
 * keys from that list, so a free-form path is refused before a row is written; offering a text
 * box would invite an operator to type something the server rejects and would quietly suggest
 * that arbitrary in-app destinations are a thing the product does.
 *
 * **On the web**, it is a URL, and that is a deliberate, bounded exception. `https` only, no
 * credentials in the address, checked here, again by the API, and a third time by the app
 * before it opens anything — because a URL field is the shape of a phishing surface with our
 * own brand on the card above it, and because `javascript:`, `intent://`, `tel:` and
 * `market://` are all real capabilities on a phone that a naive opener will fire. The app also
 * prints the host on the card, so a customer sees where a tap is taking them before they take
 * it.
 *
 * ## The colours cannot be got wrong, and that is enforced rather than advised
 *
 * `checkBannerLegibility` is three ratios: the text against the banner, and the banner against
 * *each* of the app's two page surfaces. The second and third are the ones this page exists
 * for. An operator has one screen in one mode; their customers are split across two, and a
 * pale banner that looks considered on a dark console has no edge at all on a light phone.
 *
 * A failing pair **cannot be saved**. Not a warning, not a confirmation — the button is off and
 * the reasons are listed underneath, each naming the mode that breaks and the direction to
 * move. A warning that can be clicked past is a warning that will be clicked past, by somebody
 * in a hurry, on the day of a campaign.
 *
 * Two things stop that being hostile. A row of presets, each one checked at module load, means
 * an operator who does not want to think about contrast never has to. And the card is drawn
 * twice, live, on both real page colours, so the rule is something they can see rather than
 * something they have to trust.
 *
 * ## Artwork
 *
 * Uploaded per banner as base64, an allowed raster mime under 400 KB, exactly like the operator
 * logo and the bank marks. It can be chosen inside the editor for a brand-new banner too — the
 * file is held and uploaded the moment the row exists, so "publish a banner with a picture on
 * it" is one pass through one form rather than a create followed by a second, separate action
 * somebody has to know to go and find.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { API_ORIGIN, USING_MOCK } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { API_PREFIX } from '../contracts/routes.js';
import type {
  AdminBanner,
  BannerDeepLink,
  BrandAssetMime,
  CreateBannerRequest,
} from '../contracts/types.js';
import {
  BANNER_PRESETS,
  DEFAULT_PRESET,
  checkBannerLegibility,
  normaliseExternalUrl,
  normaliseHex,
} from '../lib/bannerContrast.js';
import { BannerPreview } from '../components/BannerPreview.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  LoadingState,
  Select,
  Switch,
  Textarea,
  type Tone,
} from '../components/ui.js';
import { ConfirmModal, Modal } from '../components/Modal.js';
import { IconBanner, IconImage } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatDateTime } from '../lib/format.js';

const MAX_BYTES = 400 * 1024;
const MAX_TITLE = 80;
const MAX_BODY = 200;

/** The raster mimes the contract accepts. Mirrors `BRAND_ASSET_MIMES` — no SVG. */
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const satisfies readonly BrandAssetMime[];

/**
 * The console's runtime mirror of `BANNER_DEEP_LINKS`, with the label an operator reads.
 *
 * `satisfies` against the contract union is what keeps it honest: adding a destination to the
 * contract without describing it here, or leaving a retired one behind, fails this file's
 * compile rather than shipping a picker that disagrees with the API.
 */
const DESTINATIONS = [
  { key: 'home', label: 'Home (no navigation)' },
  { key: 'deposit_naira', label: 'Add money by bank transfer' },
  { key: 'deposit_crypto', label: 'Add money by crypto deposit' },
  { key: 'convert', label: 'Convert between balances' },
  { key: 'swap', label: 'Swap one coin for another' },
  { key: 'sell', label: 'Sell crypto for naira' },
  { key: 'send', label: 'Send to another customer' },
  { key: 'bills', label: 'Pay a bill' },
  { key: 'giftcards', label: 'Redeem a gift card' },
  { key: 'trade', label: 'Trade' },
  { key: 'activity', label: 'Activity history' },
  { key: 'support', label: 'Support' },
  { key: 'security', label: 'Security settings' },
  { key: 'account', label: 'Account' },
] as const satisfies readonly { key: BannerDeepLink; label: string }[];

const DESTINATION_LABELS: Readonly<Record<BannerDeepLink, string>> = Object.fromEntries(
  DESTINATIONS.map((d) => [d.key, d.label]),
) as Record<BannerDeepLink, string>;

function isAllowedMime(mime: string): mime is BrandAssetMime {
  return (ALLOWED_MIMES as readonly string[]).includes(mime);
}

/** Read a File as bare base64 (no `data:` prefix), for the upload body. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * The absolute URL for a banner's stored artwork.
 *
 * The API returns an API-relative *path* because only the client knows which origin and prefix
 * it is talking to. Against the in-memory demo backend there are no stored bytes at all, so
 * there is nothing to point at and the row keeps its placeholder.
 */
function imageSrc(banner: AdminBanner): string | null {
  if (USING_MOCK || banner.imagePath === null) return null;
  return `${API_ORIGIN}${API_PREFIX}${banner.imagePath}`;
}

/** What the row says about a banner, and why. `live` is the server's answer, not ours. */
function statusOf(banner: AdminBanner): { label: string; tone: Tone; hint: string } {
  if (!banner.active) {
    return { label: 'Paused', tone: 'neutral', hint: 'Switched off. Not on any phone.' };
  }
  if (banner.live) {
    return { label: 'Live', tone: 'green', hint: 'Showing on the home screen now.' };
  }
  if (banner.startsAt !== null && Date.parse(banner.startsAt) > Date.now()) {
    return { label: 'Scheduled', tone: 'blue', hint: `Starts ${formatDateTime(banner.startsAt)}` };
  }
  if (banner.endsAt !== null && Date.parse(banner.endsAt) <= Date.now()) {
    return { label: 'Finished', tone: 'amber', hint: `Ended ${formatDateTime(banner.endsAt)}` };
  }
  return { label: 'Not showing', tone: 'neutral', hint: 'Outside its schedule.' };
}

/** ISO → the `YYYY-MM-DDTHH:mm` a `datetime-local` input wants, in the operator's zone. */
function toLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const at = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/**
 * A `datetime-local` value back to an absolute instant.
 *
 * The input is naive local time, so it is interpreted in the operator's own timezone and sent
 * as a UTC instant. An operator in Lagos typing 9am means 9am in Lagos, and that is what the
 * server stores and compares against.
 */
function fromLocalInput(value: string): string | null {
  if (value === '') return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The host the app would print under the copy, for the preview. */
function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** The editor's fields. Separate from the DTO because a draft has no id or timestamps. */
interface BannerDraft {
  title: string;
  body: string;
  deepLink: BannerDeepLink;
  /**
   * Which kind of destination this is.
   *
   * Held as its own field rather than inferred from whether the URL is empty: an operator who
   * types a URL, changes their mind and switches back to an in-app screen should not have the
   * form silently keep the URL live, and one who clears the URL mid-edit should not have the
   * whole section disappear from under them.
   */
  linkKind: 'app' | 'web';
  externalUrl: string;
  backgroundColor: string;
  textColor: string;
  active: boolean;
  startsAtLocal: string;
  endsAtLocal: string;
}

const EMPTY_DRAFT: BannerDraft = {
  title: '',
  body: '',
  deepLink: 'home',
  linkKind: 'app',
  externalUrl: '',
  // A new banner opens on a preset, so the form is never in a failing state the operator did
  // not create.
  backgroundColor: DEFAULT_PRESET.backgroundColor,
  textColor: DEFAULT_PRESET.textColor,
  active: true,
  startsAtLocal: '',
  endsAtLocal: '',
};

function draftOf(banner: AdminBanner): BannerDraft {
  return {
    title: banner.title,
    body: banner.body ?? '',
    deepLink: banner.deepLink,
    linkKind: banner.externalUrl === null ? 'app' : 'web',
    externalUrl: banner.externalUrl ?? '',
    backgroundColor: banner.backgroundColor,
    textColor: banner.textColor,
    active: banner.active,
    startsAtLocal: toLocalInput(banner.startsAt),
    endsAtLocal: toLocalInput(banner.endsAt),
  };
}

export function BannersPage(): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.listBanners(), []);

  const [editing, setEditing] = useState<AdminBanner | null>(null);
  const [draft, setDraft] = useState<BannerDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminBanner | null>(null);

  /**
   * Artwork chosen inside the editor but not yet uploaded.
   *
   * A new banner has no id, so there is nothing to upload *to* until it has been created. The
   * file is held here with a preview URL and sent the moment the row exists, which is what
   * makes "publish a banner with a picture on it" one pass through one form.
   */
  const [pendingImage, setPendingImage] = useState<{ file: File; preview: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Object URLs are a leak if they are not revoked, and this one is replaced every time the
  // operator picks a different file.
  useEffect(
    () => () => {
      if (pendingImage !== null) URL.revokeObjectURL(pendingImage.preview);
    },
    [pendingImage],
  );

  const banners = useMemo(() => state.data?.banners ?? [], [state.data]);
  const liveCount = banners.filter((b) => b.live).length;
  const scheduledCount = banners.filter(
    (b) => b.active && !b.live && b.startsAt !== null && Date.parse(b.startsAt) > Date.now(),
  ).length;

  /** The live verdict on the draft's colours. Recomputed on every keystroke; it is pure maths. */
  const verdict = useMemo(
    () =>
      draft === null
        ? null
        : checkBannerLegibility({
            backgroundColor: draft.backgroundColor,
            textColor: draft.textColor,
          }),
    [draft],
  );

  /** The live verdict on the destination, when it is a web address. */
  const urlCheck = useMemo(() => {
    if (draft === null || draft.linkKind !== 'web') return null;
    return normaliseExternalUrl(draft.externalUrl);
  }, [draft]);

  const urlError = urlCheck !== null && 'error' in urlCheck ? urlCheck.error : null;
  const resolvedUrl = urlCheck !== null && 'url' in urlCheck ? urlCheck.url : null;

  /**
   * Whether Publish is allowed at all.
   *
   * Everything the operator can get wrong is folded in here rather than checked on click, so
   * the button reflects the form's state continuously and a refusal is never a surprise
   * delivered after the fact.
   */
  const canSave =
    draft !== null &&
    draft.title.trim() !== '' &&
    verdict !== null &&
    verdict.ok &&
    (draft.linkKind === 'app' || resolvedUrl !== null);

  function replaceRow(updated: AdminBanner): void {
    state.setData((prev) =>
      prev === null
        ? prev
        : { ...prev, banners: prev.banners.map((b) => (b.id === updated.id ? updated : b)) },
    );
  }

  function clearPendingImage(): void {
    setPendingImage((prev) => {
      if (prev !== null) URL.revokeObjectURL(prev.preview);
      return null;
    });
    if (fileRef.current !== null) fileRef.current.value = '';
  }

  function openNew(): void {
    setEditing(null);
    clearPendingImage();
    setDraft({ ...EMPTY_DRAFT });
  }

  function openEdit(banner: AdminBanner): void {
    setEditing(banner);
    clearPendingImage();
    setDraft(draftOf(banner));
  }

  function closeEditor(): void {
    clearPendingImage();
    setDraft(null);
    setEditing(null);
  }

  /** Hold a chosen file, after the two checks that save a round trip. The server sniffs anyway. */
  function chooseImage(file: File | undefined): void {
    if (file === undefined) return;
    if (!isAllowedMime(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File too large', 'Keep a banner image under 400 KB.');
      return;
    }
    setPendingImage((prev) => {
      if (prev !== null) URL.revokeObjectURL(prev.preview);
      return { file, preview: URL.createObjectURL(file) };
    });
  }

  async function save(): Promise<void> {
    if (draft === null || verdict === null) return;

    const title = draft.title.trim();
    if (title === '') {
      toast.error('Add a title', 'A banner needs a headline customers can read.');
      return;
    }
    if (!verdict.ok) {
      // Unreachable through the button, which is disabled. Kept because a form is not a
      // security boundary and neither is a disabled attribute.
      toast.error('Fix the colours first', verdict.failures[0]?.message ?? 'Those colours fail.');
      return;
    }
    if (draft.linkKind === 'web' && resolvedUrl === null) {
      toast.error('Check the web address', urlError ?? 'That address will not open.');
      return;
    }

    const startsAt = fromLocalInput(draft.startsAtLocal);
    const endsAt = fromLocalInput(draft.endsAtLocal);
    // Caught here as well as on the server so an operator sees it beside the two fields rather
    // than as a rejected save. The server is still the authority.
    if (startsAt !== null && endsAt !== null && Date.parse(endsAt) <= Date.parse(startsAt)) {
      toast.error('Check the dates', 'The end has to come after the start.');
      return;
    }

    const body = draft.body.trim();
    const payload: CreateBannerRequest = {
      title,
      body: body === '' ? null : body,
      deepLink: draft.deepLink,
      // Normalised, so what is stored is what the contract's schema demands rather than
      // whatever case the operator happened to type.
      backgroundColor: normaliseHex(draft.backgroundColor) ?? DEFAULT_PRESET.backgroundColor,
      textColor: normaliseHex(draft.textColor) ?? DEFAULT_PRESET.textColor,
      externalUrl: draft.linkKind === 'web' ? resolvedUrl : null,
      active: draft.active,
      // New banners join the end of the running order; existing ones keep their rank, which
      // the arrows own.
      sortOrder: editing?.sortOrder ?? banners.length,
      startsAt,
      endsAt,
    };

    setSaving(true);
    try {
      if (editing === null) {
        let created = await api.createBanner(payload);
        // The row exists now, so the held file finally has somewhere to go.
        if (pendingImage !== null) {
          created = await uploadTo(created.id, pendingImage.file);
        }
        state.setData((prev) =>
          prev === null ? prev : { ...prev, banners: [...prev.banners, created] },
        );
        toast.success('Banner published', `${created.title} is now in the running order.`);
      } else {
        let updated = await api.updateBanner(editing.id, payload);
        if (pendingImage !== null) {
          updated = await uploadTo(editing.id, pendingImage.file);
        }
        replaceRow(updated);
        toast.success('Banner saved', `${updated.title} has been updated.`);
      }
      closeEditor();
    } catch (err) {
      toast.error('Could not save', messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  async function uploadTo(id: string, file: File): Promise<AdminBanner> {
    const dataBase64 = await toBase64(file);
    return api.uploadBannerImage(id, { mime: file.type as BrandAssetMime, dataBase64 });
  }

  async function toggleActive(banner: AdminBanner, next: boolean): Promise<void> {
    setBusyId(banner.id);
    try {
      replaceRow(await api.updateBanner(banner.id, { active: next }));
    } catch (err) {
      toast.error('Could not update', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Move a banner one place in the running order.
   *
   * The whole list is renumbered 0..n-1 and only the rows whose rank actually changed are sent.
   * Renumbering rather than swapping two values is what keeps the order total: seed data, two
   * banners created in the same minute, or an older row can all share a rank, and a swap
   * between equal ranks would look like it did nothing.
   */
  async function move(banner: AdminBanner, delta: -1 | 1): Promise<void> {
    const ordered = [...banners];
    const from = ordered.findIndex((b) => b.id === banner.id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= ordered.length) return;

    const moved = ordered[from];
    const displaced = ordered[to];
    if (moved === undefined || displaced === undefined) return;
    ordered[from] = displaced;
    ordered[to] = moved;

    const changed = ordered
      .map((b, index) => ({ banner: b, sortOrder: index }))
      .filter((row) => row.banner.sortOrder !== row.sortOrder);

    setBusyId(banner.id);
    try {
      const saved = await Promise.all(
        changed.map((row) => api.updateBanner(row.banner.id, { sortOrder: row.sortOrder })),
      );
      const bySavedId = new Map(saved.map((b) => [b.id, b]));
      state.setData((prev) =>
        prev === null ? prev : { ...prev, banners: ordered.map((b) => bySavedId.get(b.id) ?? b) },
      );
    } catch (err) {
      toast.error('Could not reorder', messageOf(err));
      state.reload();
    } finally {
      setBusyId(null);
    }
  }

  /** The row-level replace, for an operator who only wants to swap the picture. */
  async function upload(banner: AdminBanner, file: File | undefined): Promise<void> {
    if (file === undefined) return;
    if (!isAllowedMime(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File too large', 'Keep a banner image under 400 KB.');
      return;
    }

    setBusyId(banner.id);
    try {
      replaceRow(await uploadTo(banner.id, file));
      toast.success('Artwork uploaded', `${banner.title} now shows its own image.`);
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (pendingDelete === null) return;
    const doomed = pendingDelete;
    setBusyId(doomed.id);
    try {
      await api.deleteBanner(doomed.id);
      state.setData((prev) =>
        prev === null ? prev : { ...prev, banners: prev.banners.filter((b) => b.id !== doomed.id) },
      );
      toast.success('Banner deleted', `${doomed.title} has been removed.`);
      setPendingDelete(null);
    } catch (err) {
      toast.error('Could not delete', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  if (state.loading) return <LoadingState label="Loading banners…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  /** What the preview draws: the held file if there is one, else whatever is stored. */
  const previewImage =
    pendingImage?.preview ?? (editing === null ? null : imageSrc(editing));

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">App banners</h1>
        <p className="page-head__sub">
          Promo cards on the home screen. Tapping one opens a screen in the app or a web page. Give a banner an end date and it retires itself.
        </p>
      </div>

      <div className="grid grid--3" style={{ marginBottom: 16 }}>
        <Card title="Showing now">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{liveCount}</div>
          <div className="faint" style={{ fontSize: 12 }}>On the home screen this minute</div>
        </Card>
        <Card title="Scheduled">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{scheduledCount}</div>
          <div className="faint" style={{ fontSize: 12 }}>Waiting for a start date</div>
        </Card>
        <Card title="Total">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{banners.length}</div>
          <div className="faint" style={{ fontSize: 12 }}>Including paused and finished</div>
        </Card>
      </div>

      <div className="toolbar">
        <div className="toolbar__grow" />
        <Button variant="primary" onClick={openNew}>
          <IconBanner size={15} /> New banner
        </Button>
      </div>

      <Card flush>
        {banners.length === 0 ? (
          <EmptyState
            glyph="📣"
            title="No banners yet"
            hint="Publish one and it appears on every customer's home screen, under the market ticker."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Artwork</th>
                  <th>Banner</th>
                  <th>Opens</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th>On</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {banners.map((banner, index) => (
                  <BannerRow
                    key={banner.id}
                    banner={banner}
                    busy={busyId === banner.id}
                    isFirst={index === 0}
                    isLast={index === banners.length - 1}
                    onMove={(delta) => void move(banner, delta)}
                    onToggle={(next) => void toggleActive(banner, next)}
                    onChoose={(file) => void upload(banner, file)}
                    onEdit={() => openEdit(banner)}
                    onDelete={() => setPendingDelete(banner)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={draft !== null}
        title={editing === null ? 'New banner' : 'Edit banner'}
        description="Keep the copy short. The card is a fixed box on a phone, so anything longer is cut off."
        onClose={closeEditor}
        size="wide"
        footer={
          <>
            <Button variant="ghost" onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={!canSave}
              onClick={() => void save()}
            >
              {editing === null ? 'Publish' : 'Save'}
            </Button>
          </>
        }
      >
        {draft !== null && verdict !== null && (
          <div className="grid grid--2">
            <div>
              <Field label="Title" htmlFor="banner-title">
                <Input
                  id="banner-title"
                  value={draft.title}
                  maxLength={MAX_TITLE}
                  placeholder="Zero fees on bank withdrawals"
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </Field>
              <Field label="Supporting line (optional)" htmlFor="banner-body">
                <Textarea
                  id="banner-body"
                  value={draft.body}
                  rows={2}
                  maxLength={MAX_BODY}
                  placeholder="One sentence under the headline."
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
              </Field>
              <div className="faint" style={{ fontSize: 12, marginTop: -6, textAlign: 'right' }}>
                {draft.body.length}/{MAX_BODY}
              </div>

              {/* ── Where it goes ── */}
              <Field label="Opens" htmlFor="banner-linkkind">
                <Select
                  id="banner-linkkind"
                  value={draft.linkKind}
                  onChange={(e) =>
                    setDraft({ ...draft, linkKind: e.target.value === 'web' ? 'web' : 'app' })
                  }
                >
                  <option value="app">A screen in the app</option>
                  <option value="web">A web page, in the customer’s browser</option>
                </Select>
              </Field>

              {draft.linkKind === 'app' ? (
                <>
                  <Field label="Which screen" htmlFor="banner-destination">
                    <Select
                      id="banner-destination"
                      value={draft.deepLink}
                      onChange={(e) =>
                        setDraft({ ...draft, deepLink: e.target.value as BannerDeepLink })
                      }
                    >
                      {DESTINATIONS.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <p className="faint" style={{ fontSize: 12, marginTop: -6 }}>
                    In-app destinations come from a fixed list. A path typed by hand is not
                    accepted, so a banner can never send anyone somewhere the app does not own.
                  </p>
                </>
              ) : (
                <>
                  <Field label="Web address" htmlFor="banner-url">
                    <Input
                      id="banner-url"
                      value={draft.externalUrl}
                      placeholder="https://genxpay.com.ng/promo"
                      spellCheck={false}
                      autoComplete="off"
                      onChange={(e) => setDraft({ ...draft, externalUrl: e.target.value })}
                    />
                  </Field>
                  {urlError !== null && draft.externalUrl.trim() !== '' ? (
                    <p
                      className="error-box"
                      role="note"
                      style={{ fontSize: 12, marginTop: -2, padding: 10 }}
                    >
                      {urlError}
                    </p>
                  ) : (
                    <p className="faint" style={{ fontSize: 12, marginTop: -6 }}>
                      Only https addresses. The app opens it in a browser inside the app and
                      shows the customer the website’s name on the card first, so they can see
                      where the tap is taking them.
                    </p>
                  )}
                </>
              )}

              {/* ── Colours ── */}
              <div className="field" style={{ marginTop: 4 }}>
                <span className="field__label">Colour</span>
                <div className="banner-presets">
                  {BANNER_PRESETS.map((preset) => {
                    const selected =
                      normaliseHex(draft.backgroundColor) === preset.backgroundColor &&
                      normaliseHex(draft.textColor) === preset.textColor;
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        className="banner-preset"
                        aria-pressed={selected}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            backgroundColor: preset.backgroundColor,
                            textColor: preset.textColor,
                          })
                        }
                      >
                        <span
                          className="banner-preset__dot"
                          style={{ background: preset.backgroundColor }}
                          aria-hidden="true"
                        />
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
                <p className="faint" style={{ fontSize: 12, marginTop: 8 }}>
                  Every one of these is legible in light mode and dark mode. Pick one, or set
                  your own below.
                </p>
              </div>

              <div className="grid grid--2">
                <ColorField
                  label="Banner colour"
                  id="banner-bg"
                  value={draft.backgroundColor}
                  onChange={(v) => setDraft({ ...draft, backgroundColor: v })}
                />
                <ColorField
                  label="Text colour"
                  id="banner-fg"
                  value={draft.textColor}
                  onChange={(v) => setDraft({ ...draft, textColor: v })}
                />
              </div>

              <Verdict verdict={verdict} />

              {/* ── Artwork ── */}
              <div className="field" style={{ marginTop: 12 }}>
                <span className="field__label">Artwork (optional)</span>
                <div
                  className="dropzone"
                  role="button"
                  tabIndex={0}
                  onClick={() => fileRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click();
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    chooseImage(e.dataTransfer.files[0]);
                  }}
                >
                  <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
                    <IconImage size={26} />
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                      {pendingImage?.file.name ?? 'Drop an image here, or click to browse'}
                    </div>
                    <div className="faint" style={{ fontSize: 12 }}>
                      PNG · JPG · WebP · max 400 KB · shown as a square
                    </div>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(e) => {
                      chooseImage(e.target.files?.[0]);
                      // Clear it, or choosing the same file twice after a failure fires no
                      // change event.
                      e.target.value = '';
                    }}
                  />
                </div>
                {pendingImage !== null && (
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" variant="ghost" onClick={clearPendingImage}>
                      Remove this image
                    </Button>
                  </div>
                )}
              </div>

              {/* ── Schedule ── */}
              <div className="grid grid--2">
                <Field label="Starts (optional)" htmlFor="banner-starts">
                  <Input
                    id="banner-starts"
                    type="datetime-local"
                    value={draft.startsAtLocal}
                    onChange={(e) => setDraft({ ...draft, startsAtLocal: e.target.value })}
                  />
                </Field>
                <Field label="Ends (optional)" htmlFor="banner-ends">
                  <Input
                    id="banner-ends"
                    type="datetime-local"
                    value={draft.endsAtLocal}
                    onChange={(e) => setDraft({ ...draft, endsAtLocal: e.target.value })}
                  />
                </Field>
              </div>
              <p className="faint" style={{ fontSize: 12, marginTop: -6 }}>
                Times are in your own timezone. Leave both empty to run until you pause it.
              </p>

              <div className="field">
                <span className="field__label">Switched on</span>
                <Switch
                  checked={draft.active}
                  label="Switched on"
                  onChange={(next) => setDraft({ ...draft, active: next })}
                />
              </div>
            </div>

            {/* ── The two previews ── */}
            <div>
              <span className="field__label">What customers see</span>
              <div className="banner-previews" style={{ marginTop: 8 }}>
                <BannerPreview
                  mode="light"
                  title={draft.title}
                  body={draft.body}
                  backgroundColor={draft.backgroundColor}
                  textColor={draft.textColor}
                  host={resolvedUrl === null ? null : hostOf(resolvedUrl)}
                  imageSrc={previewImage}
                />
                <BannerPreview
                  mode="dark"
                  title={draft.title}
                  body={draft.body}
                  backgroundColor={draft.backgroundColor}
                  textColor={draft.textColor}
                  host={resolvedUrl === null ? null : hostOf(resolvedUrl)}
                  imageSrc={previewImage}
                />
              </div>
              <p className="faint" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
                Both are the real card, at the size the phone draws it, on the app’s own two
                page colours. Customers choose their own mode, so a banner has to work in both.
              </p>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete this banner?"
        {...(pendingDelete === null
          ? {}
          : {
              description: `"${pendingDelete.title}" will be removed from every home screen. This cannot be undone, though the audit log keeps a record of it.`,
            })}
        confirmLabel="Delete"
        danger
        loading={pendingDelete !== null && busyId === pendingDelete.id}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </>
  );
}

/**
 * The verdict panel: whether these colours will publish, and if not, exactly why.
 *
 * Always rendered, in both states. A panel that only appeared on failure would make the
 * passing case feel like the absence of feedback rather than like an answer, and the ratios
 * are worth showing either way — an operator a tenth of a point from failing should be able to
 * see that they are.
 */
function Verdict({
  verdict,
}: {
  verdict: ReturnType<typeof checkBannerLegibility>;
}): JSX.Element {
  const { ratios } = verdict;
  return (
    <div
      className={`banner-verdict ${verdict.ok ? 'banner-verdict--ok' : 'banner-verdict--bad'}`}
      role={verdict.ok ? undefined : 'alert'}
      style={{ marginTop: 4 }}
    >
      <div className="banner-verdict__head">
        {verdict.ok
          ? 'Readable in light mode and dark mode.'
          : 'These colours cannot be published yet.'}
      </div>
      {!verdict.ok && (
        <ul className="banner-verdict__list">
          {verdict.failures.map((failure) => (
            <li key={failure.check}>{failure.message}</li>
          ))}
        </ul>
      )}
      <div className="banner-verdict__ratios">
        <span>text {ratios.textOnBackground ?? 'not measured'}:1</span>
        <span>on light {ratios.backgroundOnLight ?? 'not measured'}:1</span>
        <span>on dark {ratios.backgroundOnDark ?? 'not measured'}:1</span>
      </div>
    </div>
  );
}

/**
 * A colour, as a swatch and a hex box.
 *
 * The native picker is what an operator actually reaches for; the text box is what they need
 * when they have a brand hex to paste. They edit one value, so they stay in step: typing in the
 * box moves the swatch, and the swatch writes the box.
 */
function ColorField({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  // `<input type="color">` refuses anything that is not a valid 6-digit hex and silently
  // resets to black, which would fight an operator halfway through typing one.
  const swatch = normaliseHex(value) ?? '#000000';
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="color-field">
        <input
          type="color"
          className="color-field__swatch"
          value={swatch}
          aria-label={`${label} swatch`}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
        <Input
          id={id}
          className="mono"
          value={value}
          maxLength={7}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      </div>
    </div>
  );
}

function BannerRow({
  banner,
  busy,
  isFirst,
  isLast,
  onMove,
  onToggle,
  onChoose,
  onEdit,
  onDelete,
}: {
  banner: AdminBanner;
  busy: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (delta: -1 | 1) => void;
  onToggle: (next: boolean) => void;
  onChoose: (file: File | undefined) => void;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const src = imageSrc(banner);
  const status = statusOf(banner);
  const host = banner.externalUrl === null ? null : hostOf(banner.externalUrl);

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size="sm" variant="ghost" disabled={isFirst || busy} onClick={() => onMove(-1)} aria-label="Move up">
            ↑
          </Button>
          <Button size="sm" variant="ghost" disabled={isLast || busy} onClick={() => onMove(1)} aria-label="Move down">
            ↓
          </Button>
        </div>
      </td>
      <td>
        <div
          style={{
            width: 72,
            height: 40,
            borderRadius: 8,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            background: 'var(--bg-sunken)',
          }}
        >
          {src === null ? (
            <IconImage size={16} />
          ) : (
            <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* The running order reads as colours too, so an operator can spot two banners in
              the same tone without opening either. */}
          <span
            className="banner-swatch"
            style={{ background: banner.backgroundColor }}
            aria-hidden="true"
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{banner.title}</div>
            {banner.body !== null && (
              <div className="faint" style={{ fontSize: 12 }}>
                {banner.body}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="muted">
        {host === null ? (
          DESTINATION_LABELS[banner.deepLink]
        ) : (
          <>
            <div>Web page</div>
            <div className="faint" style={{ fontSize: 11 }}>
              {host}
            </div>
          </>
        )}
      </td>
      <td>
        <Badge tone={status.tone}>{status.label}</Badge>
        <div className="faint" style={{ fontSize: 11 }}>
          {status.hint}
        </div>
      </td>
      <td className="muted" style={{ fontSize: 12 }}>
        {banner.startsAt === null && banner.endsAt === null ? (
          'Always'
        ) : (
          <>
            <div>{banner.startsAt === null ? 'From now' : `From ${formatDateTime(banner.startsAt)}`}</div>
            <div>{banner.endsAt === null ? 'No end date' : `Until ${formatDateTime(banner.endsAt)}`}</div>
          </>
        )}
      </td>
      <td>
        <Switch
          checked={banner.active}
          disabled={busy}
          label={`Switch ${banner.title} ${banner.active ? 'off' : 'on'}`}
          onChange={onToggle}
        />
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Button size="sm" variant="ghost" loading={busy} onClick={() => inputRef.current?.click()}>
          {banner.imagePath === null ? 'Add image' : 'Replace'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onEdit}>
          Edit
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={onDelete}>
          Delete
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            onChoose(e.target.files?.[0]);
            // Clear it, or choosing the same file twice after a failure fires no change event.
            e.target.value = '';
          }}
        />
      </td>
    </tr>
  );
}
